import { parseDuration } from './vast-util';
import { MediaFile } from './media-file';
import VastParser from '@ogury/vast-library/parser';
import { logMessage } from '../../../../common/js/sentry';
import { VastEndScreenResolver } from './vast-end-screen.resolver';

/**
 * VASTParser
 * Wrapper of the DavidBabel VASTParser to add specific methods
 */

export type VastUrlMacros = Array<{ key: string; value: string }>;

interface VastParserOptions {
  macrosToReplace: VastUrlMacros;
  timeout?: number;
  timeoutStrategy?: 'exponential' | 'fixed';
}

export default class VASTParser {
  _vastLibraryParser!: VastParser;
  _supportedEvents: string[];
  _vastUrlMacros: VastUrlMacros;
  _timeout?: number;
  _timeoutStrategy?: 'exponential' | 'fixed';
  _retries?: number;
  _retryDelay?: number;
  _maxTotalTime?: number;

  constructor(
    supportedEvents: string[],
    vastUrlMacros: VastUrlMacros,
    timeout?: number,
    timeoutStrategy?: 'exponential' | 'fixed',
    retries?: number,
    retryDelay?: number,
    maxTotalTime?: number
  ) {
    this._supportedEvents = supportedEvents;
    this._vastUrlMacros = vastUrlMacros || [];
    this._timeout = timeout;
    this._timeoutStrategy = timeoutStrategy;
    this._retries = retries;
    this._retryDelay = retryDelay;
    this._maxTotalTime = maxTotalTime;
  }

  _buildVastParserOptions(includeTimeout: boolean = false) {
    return {
      macrosToReplace: this._vastUrlMacros,
      ...(includeTimeout && { timeout: this._timeout }),
      ...(includeTimeout && { timeoutStrategy: this._timeoutStrategy }),
      retries: this._retries,
      retryDelay: this._retryDelay,
      maxTotalTime: this._maxTotalTime
    };
  }

  addVastURL(vastUrl: string): void {
    this._vastLibraryParser = new VastParser(this._buildVastParserOptions());
    this._vastLibraryParser.parseSync(vastUrl);
  }

  addVastURLAsync(vastUrl: string): Promise<VastParser> {
    this._vastLibraryParser = new VastParser(
      this._buildVastParserOptions(true)
    );

    return new Promise((resolve, reject) => {
      this._vastLibraryParser.parseAsync(
        vastUrl,
        (parser: VastParser, error?: Error) => {
          if (error) {
            reject(error);
          } else {
            resolve(parser);
          }
        }
      );
    });
  }

  addVastContent(vastContent: string): void {
    this._vastLibraryParser = new VastParser(this._buildVastParserOptions());
    this._vastLibraryParser.parseXMLSync(vastContent);
  }

  addVastContentAsync(vastContent: string): Promise<VastParser> {
    this._vastLibraryParser = new VastParser(
      this._buildVastParserOptions(true)
    );

    return new Promise((resolve, reject) => {
      return this._vastLibraryParser.parseXMLAsync(
        vastContent,
        (parser: VastParser, error?: Error) => {
          if (error) {
            reject(error);
          } else {
            resolve(parser);
          }
        }
      );
    });
  }

  getDuration(): number {
    const durationContents = this._vastLibraryParser.getContents(['Duration']);
    return durationContents.length ? parseDuration(durationContents[0]) : 0;
  }

  getMediaFiles(): MediaFile[] {
    const mediaFilesVastElements = this._vastLibraryParser.getVastElements([
      'MediaFiles',
      'MediaFile'
    ]);

    return mediaFilesVastElements.map(
      (mediaFileVastElement): MediaFile => ({
        width: parseInt(mediaFileVastElement.getAttr('width'), 10) || 0,
        height: parseInt(mediaFileVastElement.getAttr('height'), 10) || 0,
        url: mediaFileVastElement.getContent(),
        type: mediaFileVastElement.getAttr('type'),
        duration: this.getDuration(),
        bitrate: parseInt(mediaFileVastElement.getAttr('bitrate'), 10) || 0,
        apiFramework: mediaFileVastElement.getAttr('apiFramework') || ''
      })
    );
  }

  getTrackingEvents(): Record<string, string[]> {
    const trackings = this._vastLibraryParser.getVastElements([
      'Linear',
      'TrackingEvents',
      'Tracking'
    ]);

    return trackings.reduce<Record<string, string[]>>((res, tracking) => {
      let eventName = tracking.getAttr('event');

      // Filter event not supported
      if (!eventName || this._supportedEvents.indexOf(eventName) === -1) {
        return res;
      }

      // Handle special case for progress event
      if (eventName === 'progress') {
        let offset = tracking.getAttr('offset');

        if (!offset) {
          return res;
        }

        offset = String(offset);

        if (offset.charAt(offset.length - 1) === '%') {
          eventName = `progress-${offset}`;
        } else {
          eventName = `progress-${Math.round(parseDuration(offset))}`;
        }
      }

      if (!res[eventName]) {
        res[eventName] = [];
      }

      res[eventName].push(tracking.getContent());

      return res;
    }, {});
  }

  getImpressionURLTemplates(): string[] {
    return this._vastLibraryParser.getContents(['Impression']);
  }

  getClickTrackingURLTemplates(): string[] {
    return this._vastLibraryParser.getContents(['ClickTracking']);
  }

  getClickThroughURLTemplate(): string[] {
    return this._vastLibraryParser.getContents(['ClickThrough']);
  }

  getErrorURLTemplates(): string[] {
    return this._vastLibraryParser.getContents(['Error']);
  }

  getViewableImpressionURLTemplates(): string[] {
    return this._vastLibraryParser.getContents([
      'ViewableImpression',
      'Viewable'
    ]);
  }

  getApiFramework(): string {
    const apiFramework = this._vastLibraryParser.getAttributes(
      ['MediaFiles', 'MediaFile'],
      'apiFramework'
    );

    if (!this.isEmptyProperty(apiFramework)) {
      return this.trimProperty(apiFramework[0]);
    } else {
      return '';
    }
  }

  /**
   * Get the AdParameters XML tag content
   */
  getAdParameters(): string {
    const adParametersContents = this._vastLibraryParser.getContents([
      'AdParameters'
    ]);

    return !this.isEmptyProperty(adParametersContents)
      ? this.trimProperty(adParametersContents[0])
      : '';
  }

  /**
   * Get skipDelay from VAST, fallback to -1
   */
  getSkipDelay(): number {
    const skipOffsets = this._vastLibraryParser.getAttributes(
      ['Creative', 'Linear'],
      'skipoffset'
    );

    if (!skipOffsets.length || !skipOffsets[0]) {
      return -1;
    }

    const skipOffset = skipOffsets[0];
    let skipDelay = -1;

    if (skipOffset.charAt(skipOffset.length - 1) === '%') {
      const percent = parseInt(skipOffset, 10);
      skipDelay = this.getDuration() * (percent / 100);
    } else {
      skipDelay = parseDuration(skipOffset);
    }

    return skipDelay;
  }

  getJSVerificationResources(): VastProperties[] {
    let jsVerificationResources: VastProperties[] = [];

    try {
      const adVerificationsVastElements = this._vastLibraryParser.getVastElements(
        ['AdVerifications', 'Verification']
      );

      jsVerificationResources = adVerificationsVastElements
        .map((verificationVastElement): VastProperties | null => {
          const vendorKey = verificationVastElement.getAttr('vendor');

          if (!vendorKey) {
            return null;
          }

          // Handle JavascriptResource vast element
          const jsResourceVastElements = verificationVastElement.getChilds(
            'JavaScriptResource'
          );
          const jsResourceVastElement = jsResourceVastElements.length
            ? jsResourceVastElements[0]
            : null;

          // Filter resource if the framework is not OMID or if content is empty
          if (
            !jsResourceVastElement ||
            jsResourceVastElement.getAttr('apiFramework') !== 'omid' ||
            !jsResourceVastElement.getContent()
          ) {
            return null;
          }

          // Handle VerificationParameters is there are not empty
          const verificationParamsVastElements = verificationVastElement.getChilds(
            'VerificationParameters'
          );
          const verificationParameters = verificationParamsVastElements.length
            ? verificationParamsVastElements[0].getContent()
            : '';

          return {
            vendorKey,
            verificationParameters,
            resourceUrl: jsResourceVastElement.getContent()
          } as any;
        })
        .filter((x): x is VastProperties => x !== null);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      logMessage('VAST parser getJavascriptResources error', {
        message
      });
    }
    return jsVerificationResources;
  }

  isEmptyProperty(vastProperty: any[]): boolean {
    return !vastProperty.length || !vastProperty[0];
  }

  trimProperty(vastProperty: any): string {
    return String(vastProperty).trim();
  }

  getVastIcon(): Record<string, any> | undefined {
    let vastIcon: Record<string, any> = {};
    try {
      const allowedPrograms = ['AdChoices'];
      const vastIconPath = ['Icons', 'Icon'];
      const iconProgram = this._vastLibraryParser.getAttributes(
        vastIconPath,
        'program'
      );
      const iconImage = this._vastLibraryParser.getContents([
        ...vastIconPath,
        'StaticResource'
      ]);

      if (
        this.isEmptyProperty(iconImage) ||
        this.isEmptyProperty(iconProgram) ||
        allowedPrograms.indexOf(iconProgram[0]) === -1
      ) {
        return undefined;
      }

      vastIcon = {
        url: this.trimProperty(iconImage[0]),
        tracking: {}
      };

      const iconClick = this._vastLibraryParser.getContents([
        ...vastIconPath,
        'IconClicks',
        'IconClickTracking'
      ]);
      if (!this.isEmptyProperty(iconClick)) {
        vastIcon.tracking = {
          ...vastIcon.tracking,
          click: this.trimProperty(iconClick[0])
        };
      }

      const iconClickThrough = this._vastLibraryParser.getContents([
        ...vastIconPath,
        'IconClicks',
        'IconClickThrough'
      ]);
      if (!this.isEmptyProperty(iconClickThrough)) {
        vastIcon.tracking = {
          ...vastIcon.tracking,
          clickThrough: this.trimProperty(iconClickThrough[0])
        };
      }

      const iconView = this._vastLibraryParser.getContents([
        ...vastIconPath,
        'IconViewTracking'
      ]);
      if (!this.isEmptyProperty(iconView)) {
        vastIcon.tracking = {
          ...vastIcon.tracking,
          view: this.trimProperty(iconView[0])
        };
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      logMessage('VAST icon parser error', {
        message
      });
    }
    return vastIcon;
  }

  getEndScreen(): any {
    return new VastEndScreenResolver(this._vastLibraryParser).resolve();
  }

  hasAdSystem(adSystem: string): boolean {
    return Boolean(
      this._vastLibraryParser
        .getVastElements(['AdSystem'])
        .find(({ content }: { content: string }) => content === adSystem)
    );
  }
}
